"use client"

import { useRouter, usePathname, useSearchParams } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'

export type Language = 'en' | 'es'

// Compatibility hook for existing components that expect the old useLanguage API.
// Provides: { language, setLanguage, t }
export function useLanguage() {
  const locale = useLocale()
  const t = useTranslations()
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  function setLanguage(lang: Language) {
    const pathWithoutLocale = pathname?.replace(/^\/[a-z]{2}/, '') || '/'
    const newPath = `/${lang}${pathWithoutLocale}`
    const qs = searchParams?.toString()
    let final = qs ? `${newPath}?${qs}` : newPath
    try {
      // Save current scroll position so the target page can restore it after locale navigation
      if (typeof window !== 'undefined' && window?.scrollY != null) {
        const key = `scroll:${pathWithoutLocale}:${lang}`
        try {
          // Try to detect a nearby element with an id to use as an anchor.
          // This is more robust than raw pixel offsets across locale navigation.
          let anchorId: string | null = null
          let anchorOffset = 0
          try {
            const x = Math.floor((window.innerWidth || 800) / 2)
            const y = Math.floor((window.innerHeight || 600) / 3)
            const els = (document.elementsFromPoint && document.elementsFromPoint(x, y)) || []
            for (const el of els) {
              if (el && (el as Element).id) {
                anchorId = (el as Element).id
                // compute the offset: how far the element is from the top of the viewport
                // so we can restore to the same relative position after navigation
                const rect = (el as Element).getBoundingClientRect()
                anchorOffset = Math.round(rect.top)
                break
              }
            }
          } catch (e) {
            anchorId = null
          }

          // store a JSON payload with anchor id, offset from anchor, and scrollY as fallback
          const payload = JSON.stringify({ anchorId, anchorOffset, y: window.scrollY })
          sessionStorage.setItem(key, payload)
          // Don't use full-page navigation - let ScrollRestorer handle positioning
        } catch (e) {
          // ignore storage errors
        }
      }
    } catch (e) {}

    router.push(final)
  }

  return {
    language: (locale as Language) ?? 'en',
    setLanguage,
    t,
  }
}
